// The destination is a hospital and, when the hospital has one, a laboratory
// department within it.
//
// The directory holds one row per (facility, message type), so a department code
// disambiguates a hospital that appears more than once — but every real
// laboratory-order facility Healthlink published for this practice has a **blank**
// DepartmentAgencyCode (KIN-1533, 2026-08-07). So the department is optional
// throughout: the picker only offers it when the directory actually has one, and the
// backend falls back to matching on hospitalId alone when it is absent.

const hasAgencyCode = (facility) => !!facility?.agencyCode?.trim();

export const buildHospitalOptions = (facilities, groupName) => {
  const hospitals = new Map();

  (facilities ?? [])
    .filter((facility) => !groupName || facility.hospitalGroupName === groupName)
    .forEach((facility) =>
      hospitals.set(String(facility.hospitalId), facility.hospitalName)
    );

  return [...hospitals.entries()]
    .sort(([, first], [, second]) =>
      String(first).localeCompare(String(second), undefined, {
        sensitivity: "base",
      })
    )
    .map(([hospitalId, hospitalName]) => ({
      label: hospitalName,
      value: hospitalId,
    }));
};

export const UNGROUPED_LABEL = "Other hospitals";

// The same hospitals as optgroups, for the app's plain Select. Grouping is native, so
// there is no reason to hand-roll a combobox for a list this short: Healthlink returns
// six lab-capable facilities, not six hundred.
//
// Returns null when there is nothing to group by — one group, or none at all — because
// a single optgroup wrapping every option is noise.
export const buildHospitalGroups = (facilities) => {
  const groups = new Map();

  (facilities ?? []).forEach((facility) => {
    const groupName = facility.hospitalGroupName || UNGROUPED_LABEL;
    const hospitals = groups.get(groupName) ?? new Map();

    hospitals.set(String(facility.hospitalId), facility.hospitalName);
    groups.set(groupName, hospitals);
  });

  if (groups.size < 2) return null;

  return [...groups.entries()]
    .sort(([first], [second]) => {
      if (first === UNGROUPED_LABEL) return 1;
      if (second === UNGROUPED_LABEL) return -1;

      return first.localeCompare(second, undefined, { sensitivity: "base" });
    })
    .map(([groupName, hospitals]) => ({
      label: groupName,
      options: [...hospitals.entries()]
        .sort(([, first], [, second]) =>
          String(first).localeCompare(String(second), undefined, {
            sensitivity: "base",
          })
        )
        .map(([hospitalId, hospitalName]) => ({
          label: hospitalName,
          value: hospitalId,
        })),
    }));
};

// The laboratories at one hospital, labelled by department name and valued by the
// code the submission sends back. A facility with no department code contributes
// nothing to choose between, so it is left out entirely rather than offered as a
// blank option — which is exactly what the picker used to show.
export const buildLaboratoryOptions = (facilities, hospitalId) =>
  (facilities ?? [])
    .filter(
      (facility) =>
        String(facility.hospitalId) === String(hospitalId) &&
        hasAgencyCode(facility)
    )
    .map((facility) => ({
      label: facility.agencyName || facility.agencyCode,
      value: facility.agencyCode,
    }))
    .sort((first, second) =>
      first.label.localeCompare(second.label, undefined, {
        sensitivity: "base",
      })
    );

// True when this hospital offers a department to choose between at all.
export const hasLaboratoryChoice = (facilities, hospitalId) =>
  buildLaboratoryOptions(facilities, hospitalId).length > 0;

// Resolves the chosen destination. An absent department code matches the hospital's
// first row, mirroring the backend's own resolveLabFacility.
export const findFacility = (facilities, hospitalId, agencyCode) => {
  const atHospital = (facilities ?? []).filter(
    (facility) => String(facility.hospitalId) === String(hospitalId)
  );

  if (!agencyCode) return atHospital[0];

  return atHospital.find((facility) => facility.agencyCode === agencyCode);
};

// Two laboratories at one hospital must be told apart, so the department is part of
// the label when there is one. The code stands in when the directory gives no name.
export const getFacilityLabel = (facility) => {
  if (!facility) return "";

  const department = facility.agencyName || facility.agencyCode;

  return department
    ? `${facility.hospitalName} — ${department}`
    : facility.hospitalName ?? "";
};
